import React, { createContext, useContext, useState, useMemo, useEffect } from "react";
import { OperationError } from "../DAO";
import { fetchBilling, fetchUsage } from "./ReportDAO";
import { UsageReport } from "./UsageReport";
import { BillingReport } from "./BillingReport";

export type ReportType =
   // report flavors
   | "USAGE" // visit action usage
   | "BILLING"; // Truncated, billable hours by program/office

export type FieldLayout = {
   readonly label: string;
   readonly toString: (item: BillingReport | UsageReport) => string;
   readonly styleHint: React.CSSProperties | null;
};

export type ReportStore = {
   readonly isLoading: boolean;
   readonly usageData: UsageReport[] | null;
   readonly billingData: BillingReport[] | null;
   readonly error: OperationError | null;

   readonly type: ReportType | null;
   readonly loadReportForType: () => Promise<void>;
   readonly setReportType: (type: ReportType) => void;
};

const reportContext = createContext<ReportStore>(null as never);

type Props = {
   children?: React.ReactNode;
};
export const ReportProvider = ({ children }: Props): JSX.Element => {
   const [usageData, setUsage] = useState<UsageReport[] | null>(null);
   const [billingData, setBillingData] = useState<BillingReport[] | null>(null);
   const [type, setType] = useState<ReportType | null>(null);

   const [error, setError] = useState<OperationError | null>(null);
   const [promise, setPromise] = useState<Promise<void> | null>(null);
   const isLoading: boolean = useMemo(() => promise !== null, [promise]);

   const setReportType = (t: ReportType) => {
      setType(t);
   };

   const loadReportForType = async (): Promise<void> => {
      if (promise) {
         // Already doing a fetch, use that one
         return promise;
      }
      setError(null);
      // Note:  Not clearing existing instances array until loading finishes

      const newPromise =
         type === "BILLING"
            ? fetchBilling()
                 .catch((err: OperationError) => {
                    setError(err);
                    return null;
                 })
                 .then((results: BillingReport[] | null) => {
                    setBillingData(results);
                    setPromise(null);
                 })
            : fetchUsage()
                 .catch((err: OperationError) => {
                    setError(err);
                    return null;
                 })
                 .then((results: UsageReport[] | null) => {
                    setUsage(results);
                    setPromise(null);
                 });
      setPromise(newPromise);
      return newPromise;
   };

   useEffect(() => {
      // listen to changes to type
      if (error == null && !isLoading && type !== null) {
         if ((type === "BILLING" && !billingData) || (type === "USAGE" && !usageData)) {
            loadReportForType();
         }
      }
   }, [type]);

   return (
      <reportContext.Provider
         value={{ isLoading, usageData, billingData, type, error, loadReportForType, setReportType }}
      >
         {children}
      </reportContext.Provider>
   );
};
export const useReportStore = (): ReportStore => {
   return useContext(reportContext);
};
